import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faMinus,
  faArrowRightFromBracket,
  faUserPen,
  faCubes,
  faPills,
  faChartLine,
  faBars,
  faXmark,
  faCoins,
  faClipboardList,
  faMoneyBillTransfer,
  faGear,
  faTruckFast,
  faFlaskVial,
} from "@fortawesome/free-solid-svg-icons";
import { NavLink } from "react-router-dom";
import { getAuthProps, signOut } from "../../utils/AuthenticationLibrary.js";

// Menu configuration object for better maintainability
const MENU_CONFIG = {
  dashboard: {
    icon: faChartLine,
    label: "Dashboard",
    path: "Dashboard",
    roles: [1, 2],
  },

  master: {
    icon: faUserPen,
    label: "Master",
    roles: [1, 2],
    subItems: [
      { label: "Product Detail", path: "ProductDetails" },
      { label: "Item Master", path: "ItemMaster" },
      { label: "Raw Material Master", path: "RawMaterial" },
      { label: "Packing Material Master", path: "PackingMaterial" },
      { label: "Party Master", path: "PartyMaster" },
      { label: "RM Formula", path: "RMFormula" },
      { label: "PM Formula", path: "ProductionPMFormula" },
      { label: "Account Master", path: "AccountMaster" },
      { label: "Account Group Master", path: "AccountGroupMaster" },
      { label: "Daybook Master", path: "DayBookMaster" },
      { label: "Party Wise Net Rate Master", path: "PartyWiseNetRateMaster" },
      { label: "HSN Master", path: "HSNCodes" },
      {
        label: "Q.C. Monogram Master",
        condition: "isPharmaSoftware",
        children: [
          { label: "Raw Material", path: "QCMonogramRawMaterial" },
          { label: "Packing Material", path: "QCMonogramPackingMaterial" },
          { label: "Finish Product", path: "QCMonogramFinishProduct" },
        ],
      },
      {
        label: "Dropdowns",
        children: [
          { label: "Transport/Courier Master", path: "Transport" },
          { label: "Test Master", path: "TestMaster", condition: "isPharmaSoftware" },
          { label: "Item Group Master", path: "ItemCategory" },
          { label: "R.M. Category Master", path: "RMCategoryMaster" },
          { label: "P.M. Category Master", path: "PMCategoryMaster" },
          { label: "Production Stage Master", path: "ProductionStage" },
          { label: "Storage Condition Master", path: "StorageCondition" },
          { label: "Mfg. Lic. Master", path: "MfgLicMaster" },
          { label: "Color Master", path: "ColourMaster" },
          { label: "Label Claim Title Master", path: "LabelClaimMaster" },
          { label: "Punch Size Master", path: "PunchSizeMaster" },
          { label: "Stereo Master", path: "StereoMaster" },
          { label: "State Master", path: "StateMaster" },
          { label: "Packing Material Size Master", path: "PackingMaterialSizeMaster" },
        ],
      },
    ],
  },

  inventory: {
    icon: faCubes,
    label: "Inventory",
    roles: [1, 3],
    condition: "!isTradingAccount",
    subItems: [
      { label: "Material G.R.N Entry [RM/PM]", path: "MaterialGRNEntry" },
      { label: "Purchase Order [RM/PM]", path: "PurchaseOrder" },
      { label: "Additional Entry [RM/PM]", path: "AdditionalEntry" },
      { label: "Inquiry Entry [RM/PM]", path: "InquiryEntry" },
      {
        label: "Reports",
        children: [
          { label: "Goods Received Register", path: "GoodsRegister" },
          { label: "Material Wise Purchase Report", path: "MaterialWisePurchaseReport" },
          { label: "Stock Ledger & Statement", path: "StockLedgerAndStatement" },
          { label: "Shortage Report", path: "ShortageReport" },
          { label: "Near Expiry Report", path: "NearExpiryReport" },
          { label: "Purchase Order Register", path: "PurchseOrderRegister" },
          { label: "Pending Purchase Order Report", path: "PendingPurchaseReport" },
        ],
      },
    ],
  },

  production: {
    icon: faPills,
    label: "Production",
    roles: [1, 4],
    condition: "!isTradingAccount",
    subItems: [
      { label: "Production Planning Entry", path: "ProductionPlanningEntry" },
      { label: "Production Requisition", path: "ProductionRequisition" },
      { label: "Packing Requisition", path: "PackingRequisition" },
      { label: "Batch Clearing Entry", path: "BatchClearingEntry" },
      { label: "Finish Product Label Printing", path: "FinishProductLabelPrinting" },
      {
        label: "Reports",
        children: [
          { label: "Material Requirement Report [RM]", path: "MaterialRequirmentReportRM" },
          { label: "Material Requirement Report [PM]", path: "MaterialRequirmentReportPM" },
          { label: "Pending Production Planning Report", path: "PendingProductionPlanningReport" },
          { label: "Production Requisition Print", path: "ProductionRequisitionPrint" },
          { label: "Packing Requisition Print", path: "PackingRequisitionPrint" },
          { label: "Production (Batch) Register with Yield", path: "ProductionBatchRegister" },
          { label: "Job Charges Report", path: "JobChargesReport" },
          { label: "Product Costing Report", path: "ProductCostingReport" },
          { label: "Batch Costing Report", path: "BatchCostingReport" },
        ],
      },
    ],
  },

  qc: {
    icon: faFlaskVial,
    label: "Q.C.",
    roles: [1, 4],
    condition: "isPharmaSoftware && !isTradingAccount",
    subItems: [
      {
        label: "Sample Entry",
        children: [
          { label: "Raw Material", path: "RawMaterialSampleEntry" },
          { label: "Packing Material", path: "PackingMaterialSampleEntry" },
          { label: "Finish Product", path: "FinishProductSampleEntry" },
        ],
      },
      {
        label: "Test Report Entry",
        children: [
          { label: "Raw Material", path: "RawMaterialTestReportEntry" },
          { label: "Packing Material", path: "PackingMaterialTestReportEntry" },
          { label: "Finish Product", path: "FinishProductTestReportEntry" },
        ],
      },
    ],
  },

  despatch: {
    icon: faTruckFast,
    label: "Despatch",
    roles: [1, 5],
    condition: "!isTradingAccount",
    subItems: [
      { label: "GST Invoice [FINISH GOODS]", path: "GSTInvoiceFinishGoods" },
      { label: "GST Invoice [RM]", path: "GSTInvoiceRM" },
      { label: "GST Invoice [PM]", path: "GSTInvoicePM" },
      { label: "Sales Order Entry", path: "SalesOrderEntry" },
      { label: "Sales Goods Return Entry", path: "SalesGoodsReturnEntry" },
      { label: "Other Delivery Challan", path: "OtherDeliveryChallan" },
      { label: "Inward post Entry", path: "InwardPostEntry" },
      { label: "Outward Post Entry", path: "OutwardPostEntry" },
      { label: "Envelope Print", path: "EnvelopePrint" },
      {
        label: "Despatch Reports",
        children: [
          { label: "Party Wise Despatch", path: "PartyWiseDespatch" },
          { label: "Item Wise Despatch", path: "ItemWiseDespatch" },
          { label: "Party Wise Monthly Sales", path: "PartyWiseMonthlySales" },
          { label: "Item Wise Monthly Sales", path: "ItemWiseMonthlySales" },
          { label: "Inward Outward Post Register", path: "InwardOutwardPostRegister" },
        ],
      },
      {
        label: "Stock Report",
        children: [
          { label: "Stock Statement", path: "StockStatementReport" },
          { label: "Stock ledger", path: "StockLedgerReport" },
          { label: "Near Expiry Stock Report", path: "NearExpiryStockReport" },
        ],
      },
    ],
  },

  // trade: {
  //   icon: faMoneyBillTransfer,
  //   label: "Trade",
  //   roles: [1, 5],
  //   condition: "isTradingAccount",
  //   subItems: [
  //     { label: "Stock Credit Entry", path: "StockCreditEntry" },
  //     { label: "GST Invoice Goods", path: "GSTInvoiceFinishGoods" },
  //     {
  //       label: "Trade Report",
  //       children: [
  //         { label: "Account Ledger", path: "AccountLedger" },
  //       ],
  //     },
  //   ],
  // },

  account: {
    icon: faMoneyBillTransfer,
    label: "Account",
    roles: [1, 5],
    subItems: [
      { label: "Receipt Entry", path: "ReceiptEntry" },
      { label: "Payment Entry", path: "PaymentEntry" },
      { label: "Contra Entry", path: "ContraEntry" },
      { label: "GST Purchase Entry [RM/PM]", path: "GstPurchaseEntryRMPM" },
      { label: "GST Purchase Entry [W/o Inventory]", path: "GstPurchaseEntryWithoutInventory" },
      { label: "General Debit Note Entry [GST]", path: "GeneralDebitNoteEntry" },
      { label: "General Credit Note Entry [GST]", path: "GeneralCreditNoteEntry" },
      { label: "J.V. Entry", path: "JVEntry" },
      {
        label: "GST Reports",
        children: [
          { label: "GST Sales Register", path: "GstSalesRegister" },
          { label: "GST Purchase Register", path: "GstPurchaseRegister" },
        ],
      },
      {
        label: "Account Report",
        children: [
          { label: "Account Ledger", path: "AccountLedger" },
          { label: "Pending Transaction Report", path: "TransactionReport" },
          { label: "Cash/Bank Book", path: "CashBankBook" },
          { label: "Group Wise Account Summary", path: "GroupWiseAccountSummary" },
          { label: "Opening Balance Report", path: "OpeningBalanceReport" },
        ],
      },
    ],
  },

  utility: {
    icon: faGear,
    label: "Utility",
    roles: [1, 2, 3, 4, 5],
    subItems: [
      { label: "User Profile", path: "UserProfile" },
      { label: "User Log", path: "UserLog" },
    ],
  },
};

function Sidebar({ setSidebarOpen, sidebarOpen }) {
  const [expandedMenus, setExpandedMenus] = useState({});
  const [expandedSubMenus, setExpandedSubMenus] = useState({});
  const [role, setRole] = useState(1);
  const [isTradingAccount, setTradingAccount] = useState(false);
  const [companyName, setCompanyName] = useState("");

  useEffect(() => {
    const cookie = getAuthProps();
    const roleId = cookie?.roleId ? Number(cookie.roleId) : 0;
    if (roleId > 0) setRole(roleId);

    setTradingAccount(cookie?.isTradingAccount || false);
    setCompanyName(cookie?.companyName || "");

    const mainMenu = localStorage.getItem("MainMenu");
    const subMenu = localStorage.getItem("SubMenu");
    if (mainMenu) {
      setExpandedMenus({ [mainMenu]: true });
      if (subMenu) {
        setExpandedSubMenus({ [subMenu]: true });
      }
    }
  }, []);

  const toggleMenu = (menuKey) => {
    setExpandedMenus((prev) => {
      const isCurrentlyExpanded = prev[menuKey];
      return isCurrentlyExpanded ? {} : { [menuKey]: true };
    });
    setExpandedSubMenus({});
  };

  const toggleSubMenu = (subMenuKey) => {
    setExpandedSubMenus((prev) => {
      const isCurrentlyExpanded = prev[subMenuKey];
      return isCurrentlyExpanded ? {} : { [subMenuKey]: true };
    });
  };

  const handleNavClick = (mainMenu, subMenu) => {
    if (mainMenu) localStorage.setItem("MainMenu", mainMenu);
    if (subMenu) localStorage.setItem("SubMenu", subMenu);
  };

  const shouldShowMenu = (menuKey) => {
    const menu = MENU_CONFIG[menuKey];
    if (!menu) return false;

    if (menu.isTradingOnly) return isTradingAccount;
    if (isTradingAccount) return false;
    if (menu.roles) return menu.roles.includes(role);

    return false;
  };

  const renderSubMenuItem = (item, parentKey, subParentKey = null) => {
    if (item.children) {
      const subMenuKey = `${parentKey}-${item.label}`;
      return (
        <li key={item.label}>
          <div
            className="nav-link cursor-pointer flex items-center justify-between hover:bg-graycustom px-3 py-2 rounded-md transition-colors"
            onClick={() => toggleSubMenu(subMenuKey)}
          >
            <span className="text-[16px] font-medium text-lightblack">{item.label}</span>
            <FontAwesomeIcon
              icon={expandedSubMenus[subMenuKey] ? faMinus : faPlus}
              className="text-xs text-lightblack"
            />
          </div>
          {expandedSubMenus[subMenuKey] && (
            <ul className="ml-4 mt-1 space-y-1">
              {item.children.map((child) => (
                <li key={child.path}>
                  <NavLink
                    to={child.path}
                    className={({ isActive }) =>
                      `block px-3 py-2 rounded-md text-sm transition-colors ${isActive
                        ? "bg-blue text-white font-semibold"
                        : "text-lightblack hover:bg-graycustom font-semibold"
                      }`
                    }
                    onClick={() => handleNavClick(parentKey, subMenuKey)}
                  >
                    {child.label}
                  </NavLink>
                </li>
              ))}
            </ul>
          )}
        </li>
      );
    }

    return (
      <li key={item.path}>
        <NavLink
          to={item.path}
          className={({ isActive }) =>
            `block px-3 py-2 rounded-md text-[16px] transition-colors ${isActive
              ? "bg-blue text-white font-medium"
              : "text-lightblack hover:bg-graycustom font-medium"
            }`
          }
          onClick={() => handleNavClick(parentKey, subParentKey)}
        >
          {item.label}
        </NavLink>
      </li>
    );
  };

  const renderMenuItem = (menuKey) => {
    const menu = MENU_CONFIG[menuKey];
    if (!shouldShowMenu(menuKey)) return null;

    const isExpanded = expandedMenus[menuKey];

    if (menuKey === "dashboard") {
      return (
        <li key={menuKey}>
          <NavLink
            to={menu.path}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-lg transition-all text-[16px] ${isActive
                ? "bg-blue text-white shadow-md"
                : "text-lightblack hover:bg-graycustom"
              }`
            }
            onClick={() => handleNavClick(menuKey)}
          >
            <FontAwesomeIcon icon={menu.icon} className="w-5 h-5" />
            {sidebarOpen && <span className="font-medium">{menu.label}</span>}
          </NavLink>
        </li>
      );
    }

    return (
      <li key={menuKey}>
        <div
          className={`flex items-center justify-between px-3 py-2 rounded-lg cursor-pointer transition-all ${isExpanded
            ? "bg-blue text-white shadow-md"
            : "text-lightblack hover:bg-graycustom"
            }`}
          onClick={() => toggleMenu(menuKey)}
        >
          <div className="flex items-center gap-3">
            <FontAwesomeIcon icon={menu.icon} className="w-5 h-5" />
            {sidebarOpen && <span className="font-medium">{menu.label}</span>}
          </div>
          {sidebarOpen && (
            <FontAwesomeIcon
              icon={isExpanded ? faMinus : faPlus}
              className="text-sm"
            />
          )}
        </div>

        {isExpanded && sidebarOpen && (
          <ul className="mt-2 ml-2.5 space-y-1 border-l-2 border-graycustom pl-2.5">
            {menu.subItems.map((item) => renderSubMenuItem(item, menuKey))}
          </ul>
        )}
      </li>
    );
  };

  if (role === 0) return null;

  return (
    <div
      className={`sidebar bg-white h-screen transition-all duration-300 shadow-lg flex flex-col ${sidebarOpen ? "lg:w-[280px] lg:min-w-[280px] max-lg:w-full" : "md:w-[65px] w-0"
        }`}
    >
      <div className="px-3 py-3 border-b border-graycustom">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="min-w-10 h-10 flex items-center justify-center rounded-lg bg-graycustom hover:bg-blue hover:text-white transition-colors"
          >
            <FontAwesomeIcon icon={sidebarOpen ? faXmark : faBars} />
          </button>

          {sidebarOpen && (
            <div className="font-semibold text-[24px] text-blue truncate line-clamp-1">
              {companyName}
            </div>
          )}
        </div>
      </div>

      <div
        className="flex-1 overflow-y-auto py-4 px-3"
      >
        <ul className="space-y-2">
          {Object.keys(MENU_CONFIG).map((menuKey) => renderMenuItem(menuKey))}
        </ul>
      </div>

      <div className="p-3">
        {sidebarOpen && (
          <img
            src="Logout.svg"
            alt="Logout"
            className="w-[230px] mx-auto"
          />
        )}
        <button
          className="w-full flex items-center justify-center gap-2 bg-blue text-white py-3 px-4 rounded-lg hover:bg-opacity-90 transition-all shadow-md font-medium"
          onClick={() => signOut()}
        >
          <FontAwesomeIcon icon={faArrowRightFromBracket} />
          {sidebarOpen && <span>Log out</span>}
        </button>
      </div>
    </div>
  );
}

export default Sidebar;